/*
 * EXPLOITATION — de la connexion au catalogue, en passant par les stocks, les
 * achats, les ventes et la caisse.
 *
 * Le decoupage suit les TROIS MODULES METIER de l'application et non un
 * comptage de lignes : comptabilite et ressources humaines ont chacune leur
 * garde de module, leur domaine de permissions et leur rythme d'evolution.
 * Ce qui reste ici est le tronc commun.
 *
 * L'ORDRE EST SIGNIFIANT. Angular resout les routes dans l'ordre de
 * declaration : `purchase-orders/new` doit preceder `purchase-orders/:id/edit`,
 * sans quoi « new » serait lu comme un identifiant.
 */
import { Routes } from '@angular/router';
import { permissionGuard } from '../core/guards/permission.guard';
import { moduleGuard } from '../core/guards/module.guard';
import { superAdminGuard } from '../core/guards/super-admin.guard';
import { roleCheckGuard } from '../core/guards/role-check.guard';
import { onboardingReverseGuard } from '../core/guards/onboarding-reverse.guard';
import { subscriptionGuard } from '../core/guards/subscription.guard';
import { noAuthGuard } from '../core/guards/no-auth.guard';
import { crmAccueilGuard } from '../features/crm/shared/crm-accueil.guard';

export const routesExploitation: Routes = [
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
  {
    path: 'login',
    canActivate: [noAuthGuard],
    loadComponent: () => import('../pages/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'pin',
    loadComponent: () => import('../pages/pin-screen/pin-screen.component').then(m => m.PinScreenComponent)
  },
  {
    path: 'register',
    canActivate: [noAuthGuard],
    loadComponent: () => import('../pages/register/register.component').then(m => m.RegisterComponent)
  },
  {
    path: 'verify-email',
    loadComponent: () => import('../pages/verify-email/verify-email.component').then(m => m.VerifyEmailComponent)
  },
  {
    path: 'no-role',
    loadComponent: () => import('../pages/no-role/no-role.component').then(m => m.NoRoleComponent)
  },
  {
    path: 'plans/public',
    loadComponent: () => import('../pages/public-pricing/public-pricing.component').then(m => m.PublicPricingComponent)
  },
  {
    path: 'onboarding',
    canActivate: [onboardingReverseGuard],
    loadChildren: () => import('../features/onboarding/onboarding.routes').then(m => m.onboardingRoutes)
  },
  {
    path: 'billing',
    loadComponent: () => import('../pages/billing/billing.component').then(m => m.BillingComponent)
  },
  {
    path: 'billing/success',
    loadComponent: () => import('../pages/billing/billing-success.component').then(m => m.BillingSuccessComponent)
  },
  {
    path: 'billing/cancel',
    loadComponent: () => import('../pages/billing/billing-cancel.component').then(m => m.BillingCancelComponent)
  },
  {
    path: 'profile',
    loadComponent: () => import('../pages/profile/profile.component').then(m => m.ProfileComponent)
  },
  {
    path: 'select-company',
    loadComponent: () => import('../features/companies/company-selection/company-selection.component').then(m => m.CompanySelectionComponent),
    canActivate: [roleCheckGuard, superAdminGuard],
  },
  {
    path: 'choose-company',
    loadComponent: () => import('../pages/choose-company/choose-company.component').then(m => m.ChooseCompanyComponent),
  },
  {
    path: 'notifications',
    loadComponent: () => import('../features/notifications/notification-center.component').then(m => m.NotificationCenterComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'DASHBOARD', action: 'READ' } }
  },
  {
    // Ecran systeme — bail hors ligne par juridiction. Ecart Y7.
    //
    // superAdminGuard en plus du roleCheckGuard : allonger un bail, c'est
    // etendre la fenetre pendant laquelle un poste vole reste exploitable, et
    // potentiellement se mettre en infraction vis-a-vis d'un regulateur. Le
    // serveur refuse aussi l'ecriture aux non super administrateurs — ce garde
    // n'est qu'une commodite, pas la protection.
    path: 'settings/offline-lease',
    loadComponent: () => import('../features/settings/offline-lease/offline-lease.component').then(m => m.OfflineLeaseSettingsComponent),
    canActivate: [roleCheckGuard, superAdminGuard],
  },
  {
    // Vue de gouvernance seulement : aucun accès aux clés ni aux valeurs déchiffrées.
    path: 'settings/crypto-policy',
    loadComponent: () => import('../features/security/crypto-policy/crypto-policy.component').then(m => m.CryptoPolicyComponent),
    canActivate: [roleCheckGuard, superAdminGuard],
  },
  {
    // Gestion des juridictions (pays, provinces, champs d'adresse / de banque / d'agent, langues, formes juridiques).
    // Reprise fidele de l'ecran de flowb2b, branchee sur le MODULE Juridictions autonome (jurisdiction-service) :
    // lecture publique par projet, ecriture par un compte administrateur PROPRE au module (fenetre « Connexion au
    // module Juridictions »). L'entree de menu et la route restent OUVERTES a quiconque a REFERENCE_DATA:READ
    // (permissionGuard, comme /reference-data) : c'est le module qui refuse les ecritures sans jeton valide.
    path: 'settings/jurisdictions',
    loadComponent: () => import('../features/settings/jurisdiction-management/jurisdiction-management.component').then(m => m.JurisdictionManagementComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'REFERENCE_DATA', action: 'READ' } }
  },
  {
    // Saisie en lot de produits (IMP-9, KONBIT-SF-e3b3766f). Route LITTÉRALE : elle précède `data-import`, dont l'enfant
    // `:id` lirait « lot » comme un identifiant d'import (saisie-lot.routes.spec.ts reconnaît de vraies URL).
    // INVENTORY_IMPORT:CREATE ici ; l'écran exige aussi PRODUCT:CREATE, comme les routes du serveur (module Plan).
    path: 'data-import/lot',
    loadComponent: () => import('../features/data-import/saisie-lot/pages/data-import-lot.component').then(m => m.DataImportLotComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVENTORY_IMPORT', action: 'CREATE' } }
  },
  {
    // Chargement de données (IMP-7) : accueil et journal, dépôt, détail d'un import. Permissions du module Plan
    // (INVENTORY_IMPORT:READ ici ; le dépôt exige :CREATE, voir data-import.routes.ts), jamais un rôle.
    path: 'data-import',
    loadChildren: () => import('../features/data-import/data-import.routes').then(m => m.dataImportRoutes),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVENTORY_IMPORT', action: 'READ' } }
  },
  {
    // Tableau de bord de la piste d'audit. StockFlow vendait « Audit Logs »
    // dans ses plans (hasAuditLogs) sans aucun ecran ni alimentation ; c'est
    // la contrepartie de cette promesse.
    //
    // Protege par permission : la piste revele qui a fait quoi et quand, y
    // compris sur des donnees comptables. Elle n'a pas a etre visible de tous.
    path: 'audit',
    loadComponent: () => import('../features/audit/audit-dashboard.component').then(m => m.AuditDashboardComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'AUDIT', action: 'READ' } }
  },
  /*
   * Une SECONDE declaration de `notifications`, SANS AUCUNE GARDE, se trouvait
   * ici. Elle etait morte — Angular resout dans l'ordre et la premiere, plus
   * haut, porte `roleCheckGuard` et `permissionGuard`. Mais elle etait morte
   * PAR ACCIDENT : une permutation, un tri alphabetique, un decoupage comme
   * celui-ci, et c'est la version sans droit qui l'emporte. Le centre de
   * notifications s'ouvrirait alors a quiconque connait l'URL.
   *
   * Retiree. La declaration gardee, ligne 73, reste seule.
   */
  {
    path: 'dashboard',
    loadComponent: () => import('../features/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [roleCheckGuard, permissionGuard, subscriptionGuard],
    data: { requiredPermission: { entity: 'DASHBOARD', action: 'READ' } }
  },
  {
    path: 'products',
    loadComponent: () => import('../features/products/product-list/product-list.component').then(m => m.ProductListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PRODUCT', action: 'READ' } }
  },
  {
    path: 'product-supplier',
    loadComponent: () => import('../features/product-supplier/product-supplier-list.component').then(m => m.ProductSupplierListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'SUPPLIER', action: 'READ' } }
  },
  {
    path: 'suppliers',
    loadComponent: () => import('../features/suppliers/supplier-list/supplier-list.component').then(m => m.SupplierListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'SUPPLIER', action: 'READ' } }
  },
  {
    path: 'customers',
    loadComponent: () => import('../features/customers/customer-list/customer-list.component').then(m => m.CustomerListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CUSTOMER', action: 'READ' } }
  },
  {
    // CRM-001 : fiche client. Pas de route littérale `customers/<mot>` à ce
    // jour ; s'il en vient une (`customers/new`…), la déclarer AU-DESSUS.
    path: 'customers/:id',
    loadComponent: () => import('../features/customers/customer-detail/customer-detail.component').then(m => m.CustomerDetailComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CUSTOMER', action: 'READ' } }
  },
  // CRM-001 — pistes, pipeline, fiche opportunité, paramètres. Aucune route
  // `crm/opportunities/<mot>` n'existe ; s'il en vient une, la déclarer AU-DESSUS
  // de `crm/opportunities/:id`.
  {
    // Entrée unique du menu : redirige vers le premier onglet permis.
    path: 'crm',
    pathMatch: 'full',
    canActivate: [roleCheckGuard, crmAccueilGuard],
    children: [],
  },
  {
    path: 'crm/dashboard',
    loadComponent: () => import('../features/crm/tableau/tableau-crm.component').then(m => m.TableauCrmComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'OPPORTUNITY', action: 'READ' } }
  },
  {
    path: 'crm/leads',
    loadComponent: () => import('../features/crm/pistes/pistes.component').then(m => m.PistesComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'LEAD', action: 'READ' } }
  },
  {
    path: 'crm/pipeline',
    loadComponent: () => import('../features/crm/pipeline/pipeline.component').then(m => m.PipelineComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'OPPORTUNITY', action: 'READ' } }
  },
  {
    path: 'crm/opportunities/:id',
    loadComponent: () => import('../features/crm/opportunite/opportunite-detail.component').then(m => m.OpportuniteDetailComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'OPPORTUNITY', action: 'READ' } }
  },
  {
    path: 'crm/settings',
    loadComponent: () => import('../features/crm/parametres/parametres-crm.component').then(m => m.ParametresCrmComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CRM_SETTINGS', action: 'READ' } }
  },
  {
    path: 'warehouses',
    loadComponent: () => import('../features/warehouses/warehouse-list/warehouse-list.component').then(m => m.WarehouseListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'WAREHOUSE', action: 'READ' } }
  },
  {
    path: 'stock-movements',
    loadComponent: () => import('../features/stock/stock-movements/stock-movement.component').then(m => m.StockMovementComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_MOVEMENT', action: 'READ' } }
  },
  {
    path: 'inventory/sessions',
    loadComponent: () => import('../features/inventory/inventory-sessions/inventory-session-list.component').then(m => m.InventorySessionListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_MOVEMENT', action: 'READ' } }
  },
  {
    path: 'inventory/sessions/:id',
    loadComponent: () => import('../features/inventory/inventory-sessions/inventory-session-detail.component').then(m => m.InventorySessionDetailComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_MOVEMENT', action: 'READ' } }
  },
  {
    path: 'inventory/transfers',
    loadComponent: () => import('../features/inventory/warehouse-transfer/warehouse-transfer.component').then(m => m.WarehouseTransferComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_MOVEMENT', action: 'CREATE' } }
  },
  {
    path: 'stock-summary',
    loadComponent: () => import('../features/stock/stock-summary/stock-summary.component').then(m => m.StockSummaryComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_SUMMARY', action: 'READ' } }
  },
  {
    path: 'stock-reservations',
    loadComponent: () => import('../features/stock/stock-reservations/stock-reservations.component').then(m => m.StockReservationsComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_SUMMARY', action: 'READ' } }
  },
  {
    path: 'stock-alerts',
    loadComponent: () => import('../features/stock/stock-alerts/stock-alerts.component').then(m => m.StockAlertsComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'STOCK_SUMMARY', action: 'READ' } }
  },
  {
    /*
     * Les prix suggeres, sous le meme droit que les alertes de stock.
     *
     * PRODUCT/READ et non un droit propre : une suggestion revele la
     * structure de couts de l'entreprise — la marge de chaque article, ce que
     * pesent ses charges. C'est de la donnee produit, et le serveur exige
     * `products:read` sur la meme route.
     */
    path: 'price-suggestions',
    loadComponent: () => import('../features/pricing/price-suggestions.component').then(m => m.PriceSuggestionsComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PRODUCT', action: 'READ' } }
  },
  {
    path: 'purchase-orders',
    loadComponent: () => import('../features/purchase-orders/purchase-order-list/purchase-order-list.component').then(m => m.PurchaseOrderListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PURCHASE_ORDER', action: 'READ' } }
  },
  {
    path: 'purchase-orders/new',
    loadComponent: () => import('../features/purchase-orders/purchase-form/purchase-form.component').then(m => m.PurchaseFormComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PURCHASE_ORDER', action: 'CREATE' } }
  },
  {
    path: 'purchase-orders/:id/edit',
    loadComponent: () => import('../features/purchase-orders/purchase-form/purchase-form.component').then(m => m.PurchaseFormComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PURCHASE_ORDER', action: 'UPDATE' } }
  },
  {
    path: 'cash-registers',
    loadComponent: () => import('../features/cash-register/cash-register-list/cash-register-list.component').then(m => m.CashRegisterListComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CASH_REGISTER', action: 'READ' }, requiredModule: 'posBackoffice' }
  },
  {
    path: 'cash-sessions',
    loadComponent: () => import('../features/cash-register/cash-session-dashboard/cash-session-dashboard.component').then(m => m.CashSessionDashboardComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CASH_SESSION', action: 'READ' }, requiredModule: 'posBackoffice' }
  },
  {
    path: 'cash-sessions/history',
    loadComponent: () => import('../features/cash-register/cash-session-history/cash-session-history.component').then(m => m.CashSessionHistoryComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CASH_SESSION', action: 'READ' }, requiredModule: 'posBackoffice' }
  },
  {
    path: 'pos',
    loadComponent: () => import('../features/sales-orders/pos/pos.component').then(m => m.POSComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'SALES_ORDER', action: 'CREATE' }, requiredModule: 'posBackoffice' }
  },
  {
    path: 'sales-orders',
    loadComponent: () => import('../features/sales-orders/sales-order-list/sales-order-list.component').then(m => m.SalesOrderListComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'SALES_ORDER', action: 'READ' }, requiredModule: 'salesOrders' }
  },
  {
    path: 'users',
    loadComponent: () => import('../features/users/user-list/user-list.component').then(m => m.UserListComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'USER', action: 'READ' } }
  },
  {
    path: 'companies',
    loadComponent: () => import('../features/companies/company-list/company-list.component').then(m => m.CompanyListComponent),
    canActivate: [roleCheckGuard, superAdminGuard],
    data: { requiredPermission: { entity: 'COMPANY', action: 'READ' } }
  },
  {
    path: 'company-profile',
    loadComponent: () => import('../features/companies/company-profile/company-profile.component').then(m => m.CompanyProfileComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'COMPANY', action: 'READ' } }
  },
  {
    path: 'company-profile-v2',
    loadComponent: () => import('../features/companies/company-profile-v2/company-profile-v2.component').then(m => m.CompanyProfileV2Component),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'COMPANY', action: 'READ' } }
  },
  {
    path: 'reference-data',
    loadChildren: () => import('../features/reference-data/reference-data.routes').then(m => m.REFERENCE_DATA_ROUTES),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'REFERENCE_DATA', action: 'READ' } }
  },
  {
    path: 'roles',
    loadChildren: () => import('../features/roles/roles.routes').then(m => m.rolesRoutes),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ROLE', action: 'READ' }, requiredModule: 'customRoles' }
  },
  {
    path: 'invoices',
    loadComponent: () => import('../features/invoices/invoice-list/invoice-list.component').then(m => m.InvoiceListComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVOICE', action: 'READ' }, requiredModule: 'invoicing' }
  },
  {
    path: 'invoices/new',
    loadComponent: () => import('../features/invoices/invoice-form/invoice-form.component').then(m => m.InvoiceFormComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVOICE', action: 'CREATE' }, requiredModule: 'invoicing' }
  },
  {
    path: 'invoices/:id/edit',
    loadComponent: () => import('../features/invoices/invoice-form/invoice-form.component').then(m => m.InvoiceFormComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVOICE', action: 'UPDATE' }, requiredModule: 'invoicing' }
  },
  {
    path: 'invoices/:id',
    loadComponent: () => import('../features/invoices/invoice-detail/invoice-detail.component').then(m => m.InvoiceDetailComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVOICE', action: 'READ' }, requiredModule: 'invoicing' }
  },
  {
    path: 'invoices/:id/studio',
    // Un seul studio pour les deux portees. Celui-ci lit `:id` et regle alors
    // le gabarit de CETTE facture ; sans identifiant, celui de l'entreprise.
    loadComponent: () => import('../features/invoices/studio-facture/studio-facture.component').then(m => m.StudioFactureComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVOICE', action: 'READ' }, requiredModule: 'invoicing' }
  },
  {
    path: 'invoice-studio',
    loadComponent: () => import('../features/invoices/studio-facture/studio-facture.component').then(m => m.StudioFactureComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'INVOICE', action: 'CREATE' }, requiredModule: 'invoicing' }
  },
  {
    path: 'settings/exchange-rates',
    loadComponent: () => import('../features/settings/exchange-rates/exchange-rates.component').then(m => m.ExchangeRatesComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'REFERENCE_DATA', action: 'READ' } }
  },
  // Ancienne adresse de l'ecran des taxes : devenue l'onglet « Regles fiscales » (les parametres de requete suivent).
  { path: 'settings/taxes', redirectTo: 'accounting/config/regles-fiscales', pathMatch: 'full' },
  {
    path: 'settings/email',
    loadComponent: () => import('../features/settings/email-config/email-config.component').then(m => m.EmailConfigComponent),
    canActivate: [roleCheckGuard, permissionGuard],
    data: { requiredPermission: { entity: 'EMAIL_CONFIG', action: 'READ' } }
  },
  {
    path: 'pricing',
    loadChildren: () => import('../features/pricing/pricing.routes').then(m => m.PRICING_ROUTES),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PRICING_RULES', action: 'READ' }, requiredModule: 'dynamicPricing' }
  },
  {
    path: 'ref-data-templates',
    loadComponent: () => import('../features/ref-data-templates/ref-data-templates.component').then(m => m.RefDataTemplatesComponent),
    canActivate: [roleCheckGuard, superAdminGuard],
  },
  {
    path: 'plans',
    loadChildren: () => import('../features/plans/plans.routes').then(m => m.PLANS_ROUTES),
    canActivate: [roleCheckGuard, superAdminGuard],
  },
  {
    // VTE-003 — rejeu manuel de la file outbox (comptabilite/stock), admin seulement.
    path: 'system-ops/outbox',
    loadComponent: () => import('../features/system-ops/outbox-replay/outbox-replay.component').then(m => m.OutboxReplayComponent),
    canActivate: [roleCheckGuard, superAdminGuard],
  },
];
